import { useCallback, useEffect, useState } from "react";
import { Button, Popconfirm, Space, Table, Typography, message } from "antd";
import { KeyOutlined, UserAddOutlined } from "@ant-design/icons";
import useOrganization from "../../hooks/useOrganization";
import {
  createPasswordResetLink,
  getMembers,
  removeMember,
  revokeInvitation,
} from "../../utils";
import { invitationLink, passwordResetLink } from "../../utils/links";
import LinkModal, { SharedLink } from "../../fragments/LinkModal";
import InviteMemberModal from "./InviteMemberModal";

interface Member {
  id: string;
  email: string;
  createdAt: string;
}

interface PendingInvitation {
  id: string;
  email: string;
  expiresAt: string;
}

const formatDate = (value: string) => new Date(value).toLocaleString();

const Members: React.FC = () => {
  const organization = useOrganization();
  const [data, setData] = useState<{ members: Member[]; invitations: PendingInvitation[] } | null>(
    null
  );
  const [inviting, setInviting] = useState(false);
  const [link, setLink] = useState<SharedLink | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await getMembers(organization));
    } catch (e) {
      message.error((e as Error).message);
      setData({ members: [], invitations: [] });
    }
  }, [organization]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (action: () => Promise<unknown>) => {
    try {
      await action();
    } catch (e) {
      message.error((e as Error).message);
    }
    await load();
  };

  const showResetLink = async (member: Member) => {
    try {
      const token = await createPasswordResetLink(organization, member.id);
      setLink({
        title: `Password reset link for ${member.email}`,
        url: passwordResetLink(token),
        note: "Send this link to them personally. It works once and expires in 24 hours. Any earlier reset link for this member stops working.",
      });
    } catch (e) {
      message.error((e as Error).message);
    }
  };

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<UserAddOutlined />} onClick={() => setInviting(true)}>
          Invite member
        </Button>
      </Space>

      <Typography.Title level={5}>Members</Typography.Title>
      <Table<Member>
        rowKey="id"
        loading={data == null}
        dataSource={data?.members}
        pagination={false}
        locale={{ emptyText: "No members yet" }}
        columns={[
          { title: "Email", dataIndex: "email" },
          { title: "Member since", dataIndex: "createdAt", render: formatDate },
          {
            title: "",
            key: "actions",
            align: "right",
            render: (_, member) => (
              <Space>
                <Button icon={<KeyOutlined />} onClick={() => showResetLink(member)}>
                  Password reset link
                </Button>
                <Popconfirm
                  title={`Remove ${member.email}?`}
                  description="They lose access immediately."
                  okText="Remove"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => run(() => removeMember(organization, member.id))}
                >
                  <Button danger>Remove</Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Typography.Title level={5} style={{ marginTop: 24 }}>
        Pending invitations
      </Typography.Title>
      <Table<PendingInvitation>
        rowKey="id"
        loading={data == null}
        dataSource={data?.invitations}
        pagination={false}
        locale={{ emptyText: "No pending invitations" }}
        columns={[
          { title: "Email", dataIndex: "email" },
          { title: "Expires", dataIndex: "expiresAt", render: formatDate },
          {
            title: "",
            key: "actions",
            align: "right",
            render: (_, invitation) => (
              <Popconfirm
                title={`Revoke the invitation for ${invitation.email}?`}
                okText="Revoke"
                okButtonProps={{ danger: true }}
                onConfirm={() => run(() => revokeInvitation(organization, invitation.id))}
              >
                <Button danger>Revoke</Button>
              </Popconfirm>
            ),
          },
        ]}
      />

      <InviteMemberModal
        open={inviting}
        onClose={(invited) => {
          setInviting(false);
          if (invited) {
            setLink({
              title: `Invitation for ${invited.email}`,
              url: invitationLink(invited.token),
              note: "Send this link to them personally. It works once and expires in 7 days. Any earlier invitation for this email stops working.",
            });
            load();
          }
        }}
      />
      <LinkModal link={link} onClose={() => setLink(null)} />
    </>
  );
};

export default Members;
